import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "@/ui/molecules/InlineHint";
import type { OpenShiftRequest } from "@/utils/models";

type Props = {
	/** The posted request this add fills, or `null`. */
	linkedRequest: OpenShiftRequest | null;
	/** That request is gone and it was not this sheet's own add that closed it. */
	isLinkedRequestGone: boolean;
	isRankingFailed: boolean;
	isRosterError: boolean;
	/** The section cannot say which job it is, so nothing may be added under it: its job's name, for the reason. */
	blockedJobLabel: string | null;
	testIdPrefix: string;
};

/**
 * The notes above the add list: which posted request the add fills — or that it is gone (one or the other: a request
 * that is gone no longer "fills" anything) — that the cost ranking or the staff list failed, and why an ambiguous job
 * section refuses.
 */
export const GapsAddStepHints: FC<Props> = ({ linkedRequest, isLinkedRequestGone, isRankingFailed, isRosterError, blockedJobLabel, testIdPrefix }) => {
	const { t } = useTranslation();
	return (
		<>
			{blockedJobLabel !== null && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone="warning" dataTestId={`${testIdPrefix}-ambiguous-job`}>
						{t("gaps.group.ambiguousJob", { job: blockedJobLabel })}
					</InlineHint>
				</Box>
			)}
			{linkedRequest !== null && !isLinkedRequestGone && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone="info" dataTestId={`${testIdPrefix}-linked`}>
						{t("gaps.addSheet.linkedHint", { count: linkedRequest.needed_nurses })}
					</InlineHint>
				</Box>
			)}
			{isLinkedRequestGone && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone="warning" dataTestId={`${testIdPrefix}-linked-gone`}>
						{t("open.shift.request.not.found")}
					</InlineHint>
				</Box>
			)}
			{isRosterError && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone="warning" dataTestId={`${testIdPrefix}-roster-failed`}>
						{t("profile.roster.unavailable")}
					</InlineHint>
				</Box>
			)}
			{isRankingFailed && (
				<Box sx={{ px: 2, pt: 1.5 }}>
					<InlineHint tone="warning" dataTestId={`${testIdPrefix}-ranking-failed`}>
						{t("gaps.addSheet.rankingFailed")}
					</InlineHint>
				</Box>
			)}
		</>
	);
};
